import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { DEFAULT_INACTIVITY_CONFIG, InactivityConfig } from '../config/inactivity.config';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private inactivityTimer: any;
  private config: InactivityConfig;
  private logoutCallback?: () => void;

  constructor(
    private router: Router,
    private ngZone: NgZone
  ) {
    // Usar configuración por defecto (5 minutos para testing)
    this.config = DEFAULT_INACTIVITY_CONFIG;
  }

  /**
   * Configurar el callback de logout para evitar dependencia circular
   */
  setLogoutCallback(callback: () => void): void {
    this.logoutCallback = callback;
  }

  startInactivityTimer(): void {
    this.resetInactivityTimer();

    // Agregar event listeners para detectar actividad
    this.config.activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        this.resetInactivityTimer();
      }, true);
    });
  }

  private resetInactivityTimer(): void {
    // Limpiar timer existente
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    // Configurar nuevo timer
    this.ngZone.runOutsideAngular(() => {
      this.inactivityTimer = setTimeout(() => {
        this.ngZone.run(() => {
          this.logoutDueToInactivity();
        });
      }, this.config.inactivityTimeoutMinutes * 60 * 1000);
    });
  }

  private logoutDueToInactivity(): void {
    // Usar el callback configurado para evitar dependencia circular
    if (this.logoutCallback) {
      this.logoutCallback();
    }

    // Redirigir al login
    this.router.navigate(['/login']);
  }

  stopInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    // Remover event listeners
    this.config.activityEvents.forEach(event => {
      document.removeEventListener(event, () => {
        this.resetInactivityTimer();
      }, true);
    });
  }

  // Método para cambiar la configuración (útil para testing y producción)
  setConfig(config: Partial<InactivityConfig>): void {
    this.config = { ...this.config, ...config };
    this.resetInactivityTimer();
  }

  // Método para cambiar solo el timeout (útil para testing)
  setInactivityTimeout(minutes: number): void {
    this.config.inactivityTimeoutMinutes = minutes;
    this.resetInactivityTimer();
  }

  // Método para obtener la configuración actual
  getConfig(): InactivityConfig {
    return { ...this.config };
  }
}
